import { faCheck, faEdit, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik } from 'formik';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { useFirstRender } from '../hooks/FirstRenderHook';
import usePropertyGroupProperty from '../hooks/usePropertyGroupProperty';
import LoadingOverlay from '../LoadingOverlay';
import PropertiesStore from '../stores/PropertiesStore';
import PropertyMap from './maps/PropertyMap';
import PropertyMapEditable from './maps/PropertyMapEditable';
import PropertyForm, { PropertyFormValidater } from './PropertyForm';
import PropertyGroups from './PropertyGroups';
import PropertyGroupUsers from './PropertyGroupUsers';
import PropertyPermissions from './PropertyPermissions';

function Property(props) {
  const [restoring, setRestoring] = useState(false);
  const [editing, setEditing] = useState(false);

  const firstRender = useFirstRender();

  const { loading, propertyGroups } = usePropertyGroupProperty(props);

  const propertyGroupsUsers = propertyGroups.flatMap((pg) => {
    return [...pg.users];
  });

  useEffect(() => {
    if (!firstRender) props.history.replace('/properties');
  }, [props.selectedDatabase]);

  useEffect(() => {
    if (!props.property.user_properties || !props.property.user_permissions) {
      props.propertiesStore.loadPropertyPermissions(
        props.property.id,
        props.selectedDatabase,
        props.bearerToken,
        props.refreshBearerToken
      );
    }
  }, [props.bearerToken, props.refreshBearerToken, props.property]);

  if (props.propertiesStore.loading)
    return <Spinner variant='primary' animation='border' />;
  else if (!props.property.user_properties || !props.property.user_permissions)
    return null;

  /**
   * Find the permissions of the authenticated user
   */
  let canWrite = false;
  let canDelete = false;
  let canWritePermissions = false;
  props.property.user_permissions.forEach((propertyPermission) => {
    if (propertyPermission.code === 'W') canWrite = true;
    if (propertyPermission.code === 'D') canDelete = true;
    if (propertyPermission.code === 'PW') canWritePermissions = true;
  });

  /**
   * Restore the deleted property using the properties API and update the property object in the
   * parent component using the update() method supplied in the props.
   */
  const restore = () => {
    setRestoring(true);
    fetch(
      process.env.REACT_APP_PROPERTIES_API_URL +
        'api/properties/' +
        props.property.id +
        '/restore',
      {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + props.bearerToken,
          SeasonalDatabase: props.selectedDatabase
        }
      }
    )
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) props.refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          props.update(data);
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
      })
      .finally(() => {
        setRestoring(false);
      });
  };

  return (
    <React.Fragment>
      <Row>
        <Col className='d-flex justify-content-between'>
          <Button variant='secondary' className='mb-2' onClick={props.close}>
            Back
          </Button>
          {canDelete && !props.property.deleted_at ? (
            <Button
              variant='outline-danger'
              className='mx-2 mb-2 '
              onClick={() => {
                if (
                  window.confirm(
                    'Are you sure you want to delete this property?'
                  )
                ) {
                  props.delete(props.property.id);
                }
              }}
            >
              Delete
            </Button>
          ) : null}
        </Col>
      </Row>
      <Row>
        <Col>
          {editing ? (
            <Formik
              initialValues={{
                // react-select options (it's not possible to use outside props)
                users: props.users,
                regions: props.regions,
                propertyKeyContacts: props.propertyKeyContacts,

                // Property Key Contact
                add_key_contact: false,
                add_key_contact_tab: 'details',
                property_key_contact: {
                  value: props.property.property_key_contact.id,
                  label: props.property.property_key_contact.user
                    ? props.property.property_key_contact.user.name
                    : props.property.property_key_contact.name
                },
                new_property_key_contact: {
                  user_type: 'fruition',
                  user: '',
                  name: '',
                  email: '',
                  phone_number: ''
                },

                // In the format that react-select requires
                region: {
                  value: props.property.region.id,
                  label: props.property.region.name
                },

                identifier: props.property.identifier,
                name: props.property.name,
                boundry: props.property.boundry
                  ? {
                      type: 'FeatureCollection',
                      features: [
                        {
                          type: 'Feature',
                          geometry: {
                            type: props.property.boundry.type,
                            coordinates: props.property.boundry.coordinates
                          }
                        }
                      ]
                    }
                  : null,
                hectares: props.property.hectares
              }}
              validate={PropertyFormValidater}
              onSubmit={(values, actions) => {
                // Build the json object to send to the API
                const boundry = Array.isArray(values.boundry?.features)
                  ? values.boundry?.features[0].geometry
                  : values.boundry?.features?.geometry;
                const postValues = {
                  region_id: values.region.value,
                  identifier: values.identifier,
                  name: values.name,
                  boundry: boundry || null,
                  hectares: values.hectares
                };

                // Use existing property key contact was selected
                if (values.property_key_contact) {
                  postValues.property_key_contact_id =
                    values.property_key_contact.value;
                }

                // Create a new property key contact using a user id
                else if (values.new_property_key_contact.user) {
                  postValues.new_property_key_contact = {
                    user_type: values.new_property_key_contact.user_type,
                    user_id: values.new_property_key_contact.user.value
                  };
                }

                // Create a new property key contact using text fields
                else {
                  postValues.new_property_key_contact = {
                    name: values.new_property_key_contact.name,
                    email: values.new_property_key_contact.email,
                    phone_number: values.new_property_key_contact.phone_number
                  };
                }

                fetch(
                  process.env.REACT_APP_PROPERTIES_API_URL +
                    'api/properties/' +
                    props.property.id,
                  {
                    method: 'PUT',
                    headers: {
                      'Content-Type': 'application/json',
                      Accept: 'application/json',
                      Authorization: 'Bearer ' + props.bearerToken,
                      service: 'fruition',
                      SeasonalDatabase: props.selectedDatabase
                    },
                    body: JSON.stringify(postValues)
                  }
                )
                  .then((res) => {
                    if (res.ok)
                      return res.json().then((data) => {
                        if (data) {
                          props.update(data);
                          setEditing(false);
                        }
                      });
                    else if (res.status === 422)
                      res.json().then((data) => {
                        if (data.errors)
                          Object.keys(data.errors).forEach((key) => {
                            actions.setFieldError(key, data.errors[key][0]);
                          });

                        actions.setSubmitting(false);
                      });
                    else if (res.status === 401) props.refreshBearerToken();
                    else throw Error(res.statusText);
                  })
                  .catch((err) => {
                    if (process.env.NODE_ENV === 'development')
                      console.error(err);
                    actions.setSubmitting(false);
                  });
              }}
            >
              {(props) => (
                <div className='card'>
                  <div className='card-header d-flex justify-content-between align-items-center'>
                    Property Information
                    <div>
                      {/* Submit button */}
                      <Button
                        variant='link'
                        className='text-success'
                        type='button'
                        onClick={props.handleSubmit}
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </Button>

                      {/* Cancel button */}
                      <Button
                        variant='link'
                        className='text-danger'
                        type='button'
                        onClick={() => setEditing(false)}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </Button>
                    </div>
                  </div>
                  <div className='card-body'>
                    <PropertyForm {...props} />
                    <div style={{ height: '400px' }}>
                      <PropertyMapEditable {...props} />
                    </div>
                  </div>

                  {props.isSubmitting ? <LoadingOverlay /> : null}
                </div>
              )}
            </Formik>
          ) : (
            <Card>
              <Card.Header className='d-flex justify-content-between align-items-center'>
                Property Information
                {canWrite ? (
                  props.property.deleted_at ? (
                    /* Restore button */
                    <div>
                      <Button
                        variant='outline-danger'
                        type='button'
                        onClick={restore}
                      >
                        Restore
                      </Button>
                    </div>
                  ) : (
                    /* Edit button */
                    <div>
                      <Button
                        variant='link'
                        type='button'
                        onClick={() => setEditing(true)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                    </div>
                  )
                ) : null}
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col>Identifier:</Col>
                  <Col>{props.property.identifier}</Col>
                </Row>
                <Row>
                  <Col>Name:</Col>
                  <Col>{props.property.name}</Col>
                </Row>
                <Row>
                  <Col>Region:</Col>
                  <Col>{props.property.region.name}</Col>
                </Row>
                <Row>
                  <Col>Key Contact:</Col>
                  <Col>
                    {props.property.property_key_contact.user
                      ? props.property.property_key_contact.user.name
                      : props.property.property_key_contact.name}
                  </Col>
                </Row>
                <Row className='mt-3' style={{ height: '400px' }}>
                  <Col>
                    <PropertyMap property={props.property} />
                  </Col>
                </Row>
              </Card.Body>

              {/* If the restoring action is still in progress display the loading overlay */}
              {restoring ? <LoadingOverlay /> : null}
            </Card>
          )}
        </Col>
        {canWritePermissions && !props.property.deleted_at ? (
          <Col>
            <PropertyPermissions
              selectedDatabase={props.selectedDatabase}
              bearerToken={props.bearerToken}
              refreshBearerToken={props.refreshBearerToken}
              property={props.property}
              update={props.update}
              users={props.users}
              propertyRoles={props.propertyRoles}
            />
          </Col>
        ) : null}
      </Row>
      <Row className='mt-3'>
        <Col>
          <PropertyGroups
            propertyGroups={propertyGroups}
            loading={loading}
            linkTo={props.linkTo}
          />
        </Col>
        <Col>
          <PropertyGroupUsers
            propertyGroupsUsers={propertyGroupsUsers}
            users={props.users}
            propertyRoles={props.propertyRoles}
            loading={props.loading}
          />
        </Col>
      </Row>
    </React.Fragment>
  );
}

Property.propTypes = {
  // Authentication
  selectedDatabase: PropTypes.string.isRequired,
  bearerToken: PropTypes.string.isRequired,
  refreshBearerToken: PropTypes.func.isRequired,
  propertiesStore: PropTypes.instanceOf(PropertiesStore).isRequired,

  // Property
  property: PropTypes.object.isRequired,
  update: PropTypes.func.isRequired,
  delete: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,

  // Other data
  users: PropTypes.array.isRequired,
  regions: PropTypes.array.isRequired,
  propertyKeyContacts: PropTypes.array.isRequired,
  propertyRoles: PropTypes.array.isRequired,

  // Helper methods
  linkTo: PropTypes.func.isRequired
};

export default observer(Property);
